// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';
import type {Moment} from 'moment';
import range from 'lodash/range';

import {CollapsibleTheme} from 'common/components/section';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {currentProjectStore} from 'common/report/stores/project-details';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {mapToSerializableObj} from 'common/util';
import {AsyncCountingSemaphore} from 'common/util/async-counting-semaphore';
import {emitTrackEvent} from 'common/util/dom';
import {MPProperties} from 'common/constants';
import {
  epochTimestampToProjectTimeZone,
  FormattedProperty,
  formatUserProperties,
  getRawPropertyName,
  getRawPropertyValue,
} from 'common/util/format-property';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {extractHashParams, getInsightsLinkUrl, groupNeighboringEvents} from 'common/report/util/profile/utils';
import {REPORT_NAME, UnformattedUserEvent} from 'common/report/util/profile/types';
import {TrackedEvents, TrackedProperties} from 'common/report/util/profile/tracking';
import {
  EVENTS,
  ProjectFeatureFlag,
  RESERVED_PROPS_TO_DISPLAY_NAME,
  ResourceType,
  SHOW_LAST_X_DAYS_FEATURE_FLAGS,
} from 'common/report/constants';
import {parseJsUrl, serializeJsUrl} from 'common/report/util/url';
import {
  ActivityDayGroup,
  ActivityGroup,
  FormattedUserEvent,
  INITIAL_NUM_EVENTS_LIMIT,
  NUM_PREVIEW_PROPERTIES,
  ProfileActivityAttrs,
  ProfileActivityState,
  ProfileActivityURLState,
  SINCE_DATE_FORMAT_STRING,
} from 'common/widgets/profile-activity/types';
import {
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS,
  PresetDateRange,
  SHOW_LAST_X_DAYS_PRESET_DATE_RANGE,
} from 'common/widgets/date-range-picker/models/presets';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FiltersOperator, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {inspectorClient} from 'common/report/queries/query-api/inspector';
import {RecordingPlayerModal, RecordingPlayerModalAsync} from 'common/widgets/recording-player';
import {getSessionRecordingBlockingInfo, isSessionRecordingEnabled} from 'common/report/util/feature-sampling';
import {IronContextRegistry} from 'common/report/context-registry';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {reporter} from 'common/report/error-reporting';
import {DateRangeType} from 'common/types/reports/bookmark';
import {Translatable} from 'common/i18n';

import {RecordingMetadata, RecordingPlayerSource} from '../recording-player/types';
import {sessionRecordingQueryUtils} from '../recording-player/query-utils';
// Import child components to register them
import {template} from './template';
import './index.styl';

const getDateRange = () => {
  const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
    currentProjectStore.value?.hasFeatureFlag(flag),
  );

  return showLastXDaysFlag
    ? SHOW_LAST_X_DAYS_PRESET_DATE_RANGE[showLastXDaysFlag[0]] ?? PresetDateRange.Last30Days
    : PresetDateRange.Last30Days;
};

const getDefaultProfileActivityState: () => ProfileActivityState = () => ({
  sentinelEvent: null,
  dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[getDateRange()]),
  enableShowMoreButton: true,
  error: false,
  renderedGroupings: new Map(),
  includedEvents: [],
  excludedEvents: [],
  loading: {
    initial: true,
    eventDefinitions: true,
    moreEvents: false,
    propertyDefinitions: true,
    recordings: true,
  },
  rawEvents: [],
  recordings: [],
});

export interface ProfileActivityJsxProps {
  attrs?: ProfileActivityAttrs;
}

export class ProfileActivity extends Component<
  ProfileActivityState,
  ProfileActivityAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  private declare queryApiPool: AsyncCountingSemaphore;
  private updateCallback = () => this.update();

  static get attrsSchema(): AttrsSchema<ProfileActivityAttrs> {
    return {
      ...super.attrsSchema,
      'data-group-id': `string`,
      'distinct-id': `string`,
      'canonical-distinct-id': `string`,
      'active-url-replay-id': `string`,
      'active-url-replay-timestamp': `string`,
      'history-limit-days': `number`,
      'sidebar-mode': `boolean`,
      'utc-offset': `number`,
      timezone: `string`,
      'profile-summary': `json`,
      'from-date': {
        type: `string`,
        default: ``,
      },
      'to-date': {
        type: `string`,
        default: ``,
      },
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  get config(): ConfigOptions<ProfileActivityState, EmptyObj, IronContextRegistry> {
    return {
      defaultState: getDefaultProfileActivityState(),
      contexts: [`currentProject` as const, `localStorageManager` as const, `i18n` as const],
      defaultContexts: {
        localStorageManager: new DummyStorageManager(),
      },
      template,
      helpers: {
        range,
        CollapsibleTheme,
        REPORT_NAME,
        NUM_PREVIEW_PROPERTIES,
        MPProperties,
        getRawPropertyValue,
        getRawPropertyName,
        mapToSerializableObj,
        getProjectId,
        getWorkspaceId,
        getDisplayTime: (secondsFromEpoch): Translatable => {
          const date = epochTimestampToProjectTimeZone(secondsFromEpoch * 1000, this.attr(`timezone`));

          return ({formatDate}) =>
            formatDate(date, {
              timeStyle: `medium`,
              hour12: true,
              timeZone: this.attr(`timezone`) || undefined,
            });
        },
        getLastVisibleFromDate: (): string => {
          const lastVisibleTime = this.state.rawEvents[this.state.rawEvents.length - 1].properties[`time`];

          return epochTimestampToProjectTimeZone(lastVisibleTime * 1000, this.attr(`timezone`)).format(
            SINCE_DATE_FORMAT_STRING,
          );
        },
        handleDateRangeSelected: (detail) => {
          const dateRange = new DateRange(detail);

          this.update({
            dateRange,
            sentinelEvent: null,
            rawEvents: [],
            loading: {
              ...this.state.loading,
              initial: true,
            },
          });
          this.updateJSURL(this.getJSURLFilters());
          this.loadUserActivity();
        },
        getSelectedHiddenEvents: () => {
          return this.state.includedEvents.length ? this.state.includedEvents : this.state.excludedEvents;
        },
        updateEventFilter: (detail): void => {
          const {action, selected, unselected} = detail;
          const eventDefinitions = this.state.loading.eventDefinitions;
          const propertyDefinitions = this.state.loading.propertyDefinitions;
          const dateRange = this.state.dateRange;

          let excludedEvents = [];
          let includedEvents = [];

          if (action === `show`) {
            includedEvents = unselected;
          } else if (action === `hide`) {
            excludedEvents = selected;
          }

          // Reset local state minus the date and re-fetch
          const defaultProfileActivityState = getDefaultProfileActivityState();
          this.update({
            ...defaultProfileActivityState,
            dateRange,
            sentinelEvent: null,
            loading: {
              ...defaultProfileActivityState.loading,
              eventDefinitions,
              propertyDefinitions,
              recordings: this.state.loading.recordings,
            },
            includedEvents,
            excludedEvents,
            recordings: this.state.recordings,
          });

          this.updateJSURL(this.getJSURLFilters());

          this.loadUserActivity();
        },
        getUserActivityDisplay: (): ActivityDayGroup[] => {
          return this.groupActivityByDayAndEvent(this.renameAndFormatEvents(this.state.rawEvents));
        },
        watchReplays: () => {
          this.showRecordings(this.state.recordings, RecordingPlayerSource.UsersProfile, true);
        },
        isSessionRecordingEnabled: (): boolean => {
          return isSessionRecordingEnabled(this.getContext(`currentProject`).get().value);
        },
        handleShowMore: async (): Promise<void> => {
          this.update({
            loading: {
              ...this.state.loading,
              moreEvents: true,
            },
          });
          this.trackEvent(TrackedEvents.ShowMoreEvents, this.activityStreamTrackingProperties());
          await this.loadUserActivity({initialPage: false});
          this.update({
            loading: {
              ...this.state.loading,
              moreEvents: false,
            },
          });
        },
        viewInInsights: () => {
          this.trackEvent(TrackedEvents.SeeUserInInsights, {
            ...this.activityStreamTrackingProperties(),
            'Profile Email': this.attr(`profile-summary`).email,
          });

          window.open(
            getInsightsLinkUrl({
              projectId: getProjectId(),
              workspaceId: getWorkspaceId(),
              distinctId: this.attr(`distinct-id`),
              dateRange: this.state.dateRange,
              includedEvents: this.state.includedEvents,
              excludedEvents: this.state.excludedEvents,
              dataGroupId: String(this.attr(`data-group-id`)),
            }),
            `_blank`,
          );
        },
        handleExpandGrouping: (ev: Event, activity: ActivityGroup): void => {
          const renderedGroupings = this.state.renderedGroupings;

          const {eventName, eventGroup} = activity;

          const {insertId, time} = eventGroup[0];

          renderedGroupings.set(insertId, true);

          this.update({
            renderedGroupings,
          });

          this.helpers.trackCollapsibleChange(ev, eventName, time, eventGroup.length > 1);
        },
        handleQueryBuilderBlockTrack: (ev: CustomEvent) => {
          const {props, eventName} = ev.detail;
          ev.stopPropagation();

          this.trackEvent(`${eventName}`, {
            ...props,
            [`usage context`]: TrackingUsageContext.Report,
          });
        },
        trackCollapsibleChange: (ev, eventName: string, time: string, isGroupedEvent: boolean): void => {
          this.trackEvent(TrackedEvents.UserEventExpanded, {
            ...this.activityStreamTrackingProperties(),
            Datetime: time,
            'Event Name': eventName,
            Grouped: isGroupedEvent,
            'Toggle type': ev.detail.state === `open` ? `Show` : `Hide`,
          });
        },
      },
      routes: {
        ':jsurl': (_, params) => {
          // ignore distinct_id and data_group_id prefix to parse out activity stream url filters
          const {jsUrl} = extractHashParams(params);
          // Only update state if we have a jsurl to prevent redirecting to an empty route
          if (jsUrl) {
            return this.handleJSURLRoute(jsUrl);
          }
          return null;
        },
        '': () => ({}), // don't update state on anything but a jsurl
      },
    };
  }

  prefixJSURL(jsUrlStr): string {
    const distinctId = this.attr(`distinct-id`);
    const dataGroupIdParam = this.attr(`data-group-id`) ? `data_group_id=${this.attr(`data-group-id`)}&` : ``;
    return `#distinct_id=${distinctId}&${dataGroupIdParam}${jsUrlStr}`;
  }

  updateJSURL(urlData) {
    history.replaceState(null, ``, this.prefixJSURL(serializeJsUrl(urlData)));
  }

  // TODO are we just gonna copy-paste this crap everywhere the mixin is used???
  handleJSURLRoute(rawJsURL) {
    let stateUpdate = {};

    const jsUrlInfo = parseJsUrl(rawJsURL);
    if (jsUrlInfo.valid) {
      if (jsUrlInfo.correctedUrl !== rawJsURL) {
        history.replaceState(null, ``, this.prefixJSURL(jsUrlInfo.correctedUrl));
        stateUpdate = {...stateUpdate, $fragment: jsUrlInfo.correctedUrl};
      }

      stateUpdate = {...stateUpdate, ...this.handleURLParamsUpdate(jsUrlInfo.data)};
    } else {
      this.navigate(``, {...stateUpdate, ...this.handleInvalidURLParams()});
    }

    this.update(stateUpdate);
  }

  handleURLParamsUpdate(urlData: ProfileActivityURLState): ProfileActivityURLState {
    const {includedEvents, excludedEvents, dateRange} = urlData;

    return {
      ...(includedEvents ? {includedEvents} : {}),
      ...(dateRange ? {dateRange: new DateRange(dateRange)} : {}),
      ...(excludedEvents ? {excludedEvents} : {}),
    };
  }

  // Default state is fine if url filters are invalid
  handleInvalidURLParams(): ProfileActivityURLState {
    return {};
  }

  getJSURLFilters(): ProfileActivityURLState {
    const dateRange = this.state.dateRange;

    // Only include includedEvents or excluded events, or neither
    const eventFilters = {};
    if (this.state.includedEvents.length > 0) {
      eventFilters[`includedEvents`] = this.state.includedEvents;
    } else if (this.state.excludedEvents.length > 0) {
      eventFilters[`excludedEvents`] = this.state.excludedEvents;
    }

    return {
      ...eventFilters,
      dateRange,
    };
  }

  public connectedCallback(): void {
    this.queryApiPool = new AsyncCountingSemaphore(1);
    if (this.attr(`sidebar-mode`)) {
      this.setConfig(`routes`, {});
    }
    super.connectedCallback();

    currentProjectStore.subscribe(this.updateCallback);

    this.eventDefinitionsStoreCallback = this.eventDefinitionsStoreCallback.bind(this);
    eventDefinitionsStore.subscribe(this.eventDefinitionsStoreCallback).fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });

    this.eventPropertyDefinitionsStoreCallback = this.eventPropertyDefinitionsStoreCallback.bind(this);
    eventPropertyDefinitionsStore.subscribe(this.eventPropertyDefinitionsStoreCallback).fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });

    // Prevents a double fetch
    if (this.attr(`distinct-id`) && !this.queryApiPool.getCount()) {
      this.loadUserActivity();
    }
  }

  public disconnectedCallback(): void {
    eventDefinitionsStore.unsubscribe(this.eventDefinitionsStoreCallback);
    eventPropertyDefinitionsStore.unsubscribe(this.eventPropertyDefinitionsStoreCallback);
    currentProjectStore.unsubscribe(this.updateCallback);

    super.disconnectedCallback();
  }

  public attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    // Prevent us from changing state/calling fetches during component mount
    if (!this.initialized || oldValue === newValue) {
      return;
    }

    switch (name) {
      case `data-group-id`: // fallthrough
      case `distinct-id`:
        this.update({
          loading: {
            ...this.state.loading,
            initial: true,
          },
        });
        // Since this is relatively rare, but could happen if we resolve to a different alias
        // ensure we show the loading state before triggering a fetch
        requestAnimationFrame(() => {
          this.loadUserActivity();
        });
        break;
      case `canonical-distinct-id`:
        this.loadUserRecordings();
        this.showActiveReplay();
        break;
    }
  }

  showActiveReplay() {
    if (this.attr(`active-url-replay-id`) && this.attr(`canonical-distinct-id`)) {
      this.showRecordingsWithMetadataAsync(
        [this.attr(`active-url-replay-id`)],
        [this.attr(`canonical-distinct-id`)],
        RecordingPlayerSource.UsersProfileLink,
        true,
        this.attr(`active-url-replay-timestamp`),
      );
    }
  }

  eventPropertyDefinitionsStoreCallback(): void {
    this.update({
      loading: {
        ...this.state.loading,
        propertyDefinitions: false,
      },
    });
  }

  eventDefinitionsStoreCallback(): void {
    this.update({
      loading: {
        ...this.state.loading,
        eventDefinitions: false,
      },
    });
  }

  showRecordings(replays: RecordingMetadata[], source: RecordingPlayerSource, enableUpdateURL = false) {
    RecordingPlayerModal({
      replays,
      localStorageManager: this.getContext(`localStorageManager`),
      source,
      enableUpdateURL,
    });
  }

  showRecordingsWithMetadataAsync(
    replayIds: string[],
    distinctIds: string[],
    source: RecordingPlayerSource,
    enableUpdateURL = false,
    startingTimestamp = null,
  ) {
    const eventsAndReplaysPromise = sessionRecordingQueryUtils
      .fetchRecordingsByIds({
        replayIds,
        distinctIds,
        blockedReplayFrames: getSessionRecordingBlockingInfo(this.getContext(`currentProject`).get().value),
        i18n: this.getContext(`i18n`),
      })
      .then((replays) => ({
        replays,
        events: [],
      }));

    RecordingPlayerModalAsync({
      eventsAndReplaysPromise,
      localStorageManager: this.getContext(`localStorageManager`),
      source,
      enableUpdateURL,
      startingTimestamp,
    });
  }

  private renameAndFormatEvents(events: UnformattedUserEvent[]): FormattedUserEvent[] {
    const timezone = this.attr(`timezone`);
    const dataGroupId = String(this.attr(`data-group-id`));
    const eventDefinitions = eventDefinitionsStore.value;
    const propertyDefinitions = eventPropertyDefinitionsStore.value;

    // Use any lexicon defined names/renames, specific mixpanel properties renames, or fallback to the property name
    const getDisplayName = (eventName: string): string =>
      eventDefinitions[eventName]?.displayName || EVENTS[eventName] || eventDefinitions[eventName]?.name || eventName;

    const formatUserEvent = (userEvent: UnformattedUserEvent): FormattedUserEvent => {
      const eventName = getDisplayName(userEvent.event);
      const properties = formatUserProperties(userEvent.properties, {
        timezone,
        dataGroupId,
        i18n: this.getContext(`i18n`),
      });

      const time = getRawPropertyValue(properties.get(`time`)) as number;
      const insertId = getRawPropertyValue(properties.get(MPProperties.InsertId)) as string;
      const userDefinedProperties: FormattedProperty[] = [];
      const mixpanelProperties: FormattedProperty[] = [];
      const replayId =
        userEvent.event === `$mp_session_record`
          ? (getRawPropertyValue(properties.get(`$mp_replay_id`)) as string)
          : null;

      for (const [propertyName, property] of properties.entries()) {
        if (
          propertyDefinitions[propertyName]?.isMixpanelDefinition ||
          Object.prototype.hasOwnProperty.call(RESERVED_PROPS_TO_DISPLAY_NAME, propertyName)
        ) {
          mixpanelProperties.push(property);
        } else {
          userDefinedProperties.push(property);
        }
      }

      return {
        eventDisplayName: eventName,
        insertId,
        ...(replayId ? {replayId} : {}),
        mixpanelProperties,
        userDefinedEventProperties: userDefinedProperties,
        time,
      };
    };

    return events.map(formatUserEvent);
  }

  private async loadUserActivity({initialPage = true}: {initialPage?: boolean} = {}): Promise<void> {
    // This can happen in some weird cases during component initialization, skip the query until we have a valid limit
    if (!this.attr(`distinct-id`) || !this.queryApiPool) {
      return;
    }

    if (await this.queryApiPool.acquire()) {
      const {events, error, sentinelEvent} = await this.queryData(initialPage);
      if (error) {
        this.update({
          error: true,
          rawEvents: [],
          sentinelEvent: null,
          loading: {
            ...this.state.loading,
            initial: false,
          },
        });
        this.queryApiPool.release();
        return;
      }

      this.update({
        rawEvents: initialPage ? events.reverse() : [...this.state.rawEvents, ...events.reverse()],
        sentinelEvent,
        enableShowMoreButton: Boolean(sentinelEvent),
        loading: {
          ...this.state.loading,
          initial: false,
        },
      });

      this.queryApiPool.release();
      this.trackEvent(TrackedEvents.QueryFinished, this.activityStreamTrackingProperties());
    }
  }

  private async queryData(firstPage = true) {
    const includedEvents = this.state.includedEvents;
    const excludedEvents = this.state.excludedEvents;
    const sentinelEvent = this.state.sentinelEvent;

    let dateRange = this.state.dateRange;
    if (this.attr(`to-date`) && this.attr(`from-date`)) {
      dateRange = new DateRange({
        type: DateRangeType.Between,
        from: this.attr(`from-date`),
        to: this.attr(`to-date`),
      });
    }

    const allEventsEntry: QueryEventEntry = {
      aggregationOperator: null,
      aggregationOperatorPerUser: null,
      dataGroupId: String(this.attr(`data-group-id`)),
      event: {
        custom: false,
        label: `All Events`,
        value: `$all_events`,
      },
      filters: [],
      filtersOperator: FiltersOperator.And,
      property: null,
      type: ResourceType.Event,
    };

    const params = {
      distinctIDs: [this.attr(`distinct-id`)],
      bookmark: {
        entries: [allEventsEntry],
        filters: [],
        filtersOperator: FiltersOperator.And,
        dateRange,
        isQuerySamplingEnabled: false,
      },
      limit: INITIAL_NUM_EVENTS_LIMIT,
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      queryReason: QueryReason.QbOtherUpdate,
      search: ``,
      searchProperties: [],
      ...(excludedEvents ? {excludedEvents} : {}),
      ...(includedEvents ? {includedEvents} : {}),
      ...(sentinelEvent && firstPage === false ? {sentinelEvent} : {}),
      useQuerySampling: false,
    };

    try {
      const {events, sentinel_event: stnEvent} = await inspectorClient.queryData(params);
      return {
        sentinelEvent: stnEvent,
        events,
        error: false,
      };
    } catch {
      return {
        error: true,
        events: null,
        sentinelEvent: null,
      };
    }
  }

  private async loadUserRecordings(): Promise<void> {
    // This can happen in some weird cases during component initialization, skip the query until we have a valid limit
    if (!this.attr(`distinct-id`) || !this.queryApiPool) {
      return;
    }

    // Group profile recordings are not currently supported
    if (this.attr(`data-group-id`)) {
      return;
    }

    // skip if customer hasn't integrated yet
    const currentProject = this.getContext(`currentProject`).get().value;
    if (currentProject && !currentProject.hasIntegratedSessionReplay) {
      this.update({recordings: [], loading: {...this.state.loading, recordings: false}});
      return;
    }

    try {
      const recordings = await sessionRecordingQueryUtils.fetchRecordingsForDistinctId(
        this.attr(`distinct-id`),
        getSessionRecordingBlockingInfo(this.getContext(`currentProject`).get().value),
        this.getContext(`i18n`),
      );
      this.update({recordings, loading: {...this.state.loading, recordings: false}});
    } catch (err) {
      this.update({recordings: [], loading: {...this.state.loading, recordings: false}});
      reporter.error(err);
    }
  }

  dayHeader(comparisonTime: Moment): Translatable {
    const now = epochTimestampToProjectTimeZone(Date.now(), this.attr(`timezone`));

    return ({t, formatDate}) => {
      const formattedTime = formatDate(comparisonTime, {
        month: `long`,
        day: `numeric`,
        year: `numeric`,
      });

      if (comparisonTime.isSame(now, `day`)) {
        return `${t(`reports.date-range.presets.today`)} · ${formattedTime}`;
      } else if (comparisonTime.isSame(now.clone().subtract(1, `day`), `day`)) {
        return `${t(`reports.date-range.presets.yesterday`)} · ${formattedTime}`;
      }

      return formattedTime;
    };
  }

  dateForEvent(time: number): Moment {
    const date = epochTimestampToProjectTimeZone(time * 1000, this.attr(`timezone`));

    return date.startOf(`day`);
  }

  groupActivityByDayAndEvent(events: FormattedUserEvent[]): ActivityDayGroup[] {
    const groupedByDate: FormattedUserEvent[][] = groupNeighboringEvents(events, (a, b) =>
      this.dateForEvent(a.time).isSame(this.dateForEvent(b.time), `day`),
    );

    const groupedByEvent: FormattedUserEvent[][][] = groupedByDate.map((eventGroup) =>
      groupNeighboringEvents(eventGroup, (a, b) => a.eventDisplayName === b.eventDisplayName),
    );

    return groupedByEvent.map(
      (group: FormattedUserEvent[][]): ActivityDayGroup => ({
        dayHeader: this.dayHeader(this.dateForEvent(group[0][0].time)),
        activities: group.map(
          (grp): ActivityGroup => ({
            eventName: grp[0].eventDisplayName,
            eventGroup: grp,
          }),
        ),
      }),
    );
  }

  activityStreamTrackingProperties(): TrackedProperties {
    const {from, to} = this.state.dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`));

    const selectivelyShowingEvents = this.state.includedEvents.length > 0;

    return {
      'Selectively Showing Events': selectivelyShowingEvents,
      '# of Included Events': selectivelyShowingEvents ? this.state.includedEvents.length : null,
      '# of Excluded Events': selectivelyShowingEvents ? null : this.state.excludedEvents.length,
      'Date range type': this.state.dateRange.type,
      'Date range unit': this.state.dateRange.window?.unit,
      'Date range value': this.state.dateRange.window?.value,
      'Date range from': from,
      'Date range to': to,
    };
  }

  // TODO(sam): use typescript template string types for typing instead of string once https://github.com/microsoft/TypeScript/pull/40336 is released
  // We can type is as ```type EventName<T extends string> = `[mp-query-builder]${T}`; ```
  trackEvent(eventName: TrackedEvents | string, properties: TrackedProperties): void {
    emitTrackEvent(this, {
      eventName,
      props: properties,
    });
  }
}

export const ProfileActivityEl = defineAndMakeJsxFactory<ProfileActivityJsxProps>(`profile-activity`, ProfileActivity);
export const ProfileActivityReact = panel2react<ProfileActivityJsxProps>(`profile-activity`, ProfileActivity);
